/**
 * Breakdown / Under-Repair service helpers.
 *
 * Pure-ish helpers used by the Breakdowns page and the GPS quick-report button.
 * They receive the useCollection callbacks (add / update) so they stay testable
 * and don't depend on React. All data is per-agency (the breakdowns collection
 * and cars collection are already agency-scoped), so isolation is automatic.
 */

import { sendTelegramNotification } from '../../shared/utils/helpers';
import { buildTelegramMessage } from '../../../utils/telegramMessages';

export const carLabelOf = (car) =>
  `${car?.brand || ''} ${car?.model || ''}${car?.plate ? ` (${car.plate})` : ''}`.trim() || 'Véhicule';

/** Human-readable repair duration from minutes. */
export const formatDuration = (minutes, lang = 'fr') => {
  const m = Math.max(0, Math.round(minutes || 0));
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  const mm = m % 60;
  const parts = [];
  if (d) parts.push(`${d}${lang === 'ar' ? 'ي' : 'j'}`);
  if (h) parts.push(`${h}${lang === 'ar' ? 'س' : 'h'}`);
  if (mm || (!d && !h)) parts.push(`${mm}${lang === 'ar' ? 'د' : 'min'}`);
  return parts.join(' ');
};

/**
 * Report a breakdown for a vehicle:
 *  - creates an OPEN breakdown record (with GPS snapshot),
 *  - switches the car to 'maintenance' (remembering its previous status),
 *  - sends an instant Telegram alert to the admin.
 */
export const reportBreakdown = async ({
  car, comment = '', severity = '', settings = {}, lang = 'fr',
  addBreakdown, updateCar, reportedBy = '',
}) => {
  if (!car || !car.id) throw new Error('no_car');

  const now = new Date();
  const lat = car.gpsLat ?? null;
  const lng = car.gpsLng ?? null;
  const address = car.gpsAddress || '';
  const mapsUrl = (lat != null && lng != null) ? `https://maps.google.com/?q=${lat},${lng}` : '';
  const label = carLabelOf(car);

  const record = {
    carId: car.id,
    carLabel: label,
    plate: car.plate || '',
    comment: comment || '',
    severity: severity || '',
    status: 'open',
    reportedAt: now.toISOString(),
    reportedBy: reportedBy || '',
    lat, lng, address,
    prevStatus: car.status && car.status !== 'maintenance' ? car.status : 'available',
  };

  const inserted = await addBreakdown(record);

  // Vehicle → Under Repair (maintenance).
  if (updateCar) {
    try { await updateCar(car.id, { status: 'maintenance' }); } catch (_) {}
  }

  // Instant admin notification (Telegram). Gated by the 'breakdown' toggle.
  try {
    const msg = buildTelegramMessage('breakdown_reported', {
      carLabel: label,
      severity,
      comment,
      address,
      mapsUrl,
      time: now.toLocaleString(lang === 'ar' ? 'ar-MA' : 'fr-FR'),
    }, settings.telegramLang || lang);
    sendTelegramNotification(msg, 'breakdown');
  } catch (_) {}

  return inserted;
};

/**
 * Close a breakdown / repair:
 *  - marks the record closed, computes the repair duration,
 *  - restores the vehicle to its previous status (default 'available'),
 *  - sends a Telegram confirmation.
 */
export const closeBreakdown = async ({
  breakdown, closeComment = '', settings = {}, lang = 'fr',
  updateBreakdown, updateCar,
}) => {
  if (!breakdown || !breakdown.id) throw new Error('no_breakdown');

  const now = new Date();
  const start = breakdown.reportedAt ? new Date(breakdown.reportedAt) : now;
  const durationMin = Math.max(0, Math.round((now - start) / 60000));

  await updateBreakdown(breakdown.id, {
    status: 'closed',
    closedAt: now.toISOString(),
    closeComment: closeComment || '',
    durationMin,
  });

  // Restore the vehicle to its pre-breakdown status.
  if (updateCar && breakdown.carId) {
    try { await updateCar(breakdown.carId, { status: breakdown.prevStatus || 'available' }); } catch (_) {}
  }

  try {
    const msg = buildTelegramMessage('breakdown_closed', {
      carLabel: breakdown.carLabel || '',
      duration: formatDuration(durationMin, lang),
      comment: closeComment,
      time: now.toLocaleString(lang === 'ar' ? 'ar-MA' : 'fr-FR'),
    }, settings.telegramLang || lang);
    sendTelegramNotification(msg, 'breakdown');
  } catch (_) {}

  return durationMin;
};
